import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { contractTemplateVariableApi } from '@pages/Admin/PlatformSettings2/SettingUpContracts/api/contractTemplateVariable.api'
import type { ConstructorCalculatorField } from '../utils/contractTemplateConstructor.utils'

const CALCULATOR_RECALC_DEBOUNCE_MS = 400

type UseCalculatorRecalculationParams = {
  calculatorFields: ConstructorCalculatorField[]
  setValues: Dispatch<SetStateAction<Record<string, string>>>
}

export const useCalculatorRecalculation = ({
  calculatorFields,
  setValues,
}: UseCalculatorRecalculationParams) => {
  const [recalculatingCodes, setRecalculatingCodes] = useState<Set<string>>(
    () => new Set(),
  )
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)

  const calculatorCodes = useMemo(
    () => new Set(calculatorFields.map((field) => field.code)),
    [calculatorFields],
  )

  const cancelRecalculation = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    requestIdRef.current += 1
    setRecalculatingCodes(new Set())
  }

  const scheduleRecalculation = (nextValues: Record<string, string>) => {
    if (!calculatorFields.length) return

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    setRecalculatingCodes(new Set(calculatorFields.map((field) => field.code)))

    timerRef.current = setTimeout(async () => {
      const requestId = ++requestIdRef.current

      const updates = await Promise.all(
        calculatorFields.map(async (field) => {
          try {
            const result = await contractTemplateVariableApi.previewCalculator({
              type: 'calculator',
              settings: { formula: field.formula },
              values: nextValues,
            })
            return { code: field.code, value: String(result.value ?? '') }
          } catch {
            return null
          }
        }),
      )

      if (requestId !== requestIdRef.current) return

      setValues((prev) => {
        const next = { ...prev }
        let changed = false
        updates.forEach((updateItem) => {
          if (!updateItem) return
          if (next[updateItem.code] === updateItem.value) return
          next[updateItem.code] = updateItem.value
          changed = true
        })
        return changed ? next : prev
      })
      setRecalculatingCodes(new Set())
    }, CALCULATOR_RECALC_DEBOUNCE_MS)
  }

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      requestIdRef.current += 1
    },
    [],
  )

  return {
    recalculatingCodes,
    calculatorCodes,
    scheduleRecalculation,
    cancelRecalculation,
  }
}
